import React, { useEffect } from 'react';
import { ICartOrderInfo } from '@adac-mp/shared';
import type {
	ClickEvent,
	ChangeEvent,
	Event,
	EventDataObj,
	DataLayerObject,
	TrackingProps,
	ICommerceEvent,
	ICommerceInput,
	ICommerceData,
	DataLayerEventName,
	OptionalCommerceData,
	IProductTrackingDataProps,
	CommerceStatus,
	AdditionalInput,
	ICommerceInputGoogleAnalytics,
	AdditionalInputGoogleAnalytics,
	OptionalCommerceDataGoogleAnalytics,
	ICommerceEventGoogleAnalytics,
} from './types';

type WithTrackingProps = {
	tracking?: TrackingProps;
};

const trackedLabel: string[] = [];

const productNameMap = {
	3: 'Maut Österreich',
	7: 'Maut Slowenien',
	6: 'Maut Tschechien',
	5: 'DKV Camper Mautbox',
	14: 'Maut Schweiz'
};

const productSupplierMap = {
	3: 'ASFINAG',
	7: 'DARS',
	6: 'SFDI',
	5: 'DKV',
	14: 'BAZG'
};

const getEventDataBasedOnType = (event: Event) => {
	if (event.trackOnce && trackedLabel.includes(event.type)) {
		return;
	}
	const { product, productVariant } = event;
	const eventAction = `${String(event.element).trim()} | ${String(event.interaction).trim()}`;
	const eventCategory = event.category || 'Funnel';
	const eventLabel = String(event.label).trim();

	const eventDataObj: EventDataObj = {
		eventData: {
			eventAction,
			eventCategory,
			eventLabel,
		},
	};

	if (product) {
		eventDataObj.eventData.product = product;
	}
	if (productVariant) {
		eventDataObj.eventData.productVariant = productVariant;
	}

	trackedLabel.push(event.element);
	// eslint-disable-next-line consistent-return
	return eventDataObj;
};

const extendProductInformationWIthConstants = (data: ICommerceInput[]): ICommerceData[] => {
	const staticData = {
		productBrand: 'ADAC SE', //Die Abteilung bzw. Gesellschaft im ADAC
		productType: 'Mautportal', //Der Produkttyp, eine Mitgliedschaft, eine Versicherung, ein Finanzprodukt
		contractType: 'einmaliger Kauf', //befristet, einmaliger kauf
		productQty: 1,
	};
	return data.map((product) => {
		return { ...product, ...staticData };
	});
};

export const trackEvent = (data: Event) => {
	if (window.dataLayer) {
		if (data.element && !(data.trackOnce && trackedLabel.includes(data.label))) {
			const eventData = getEventDataBasedOnType(data);
			const event: DataLayerObject = {
				...eventData,
				event: data.type,
			};
			window.dataLayer.push(event);
			trackedLabel.push(data.label);
		}
	}
};

export const trackCommerce = (data: ICommerceEvent) => {
	if (window.dataLayer) {
		if (data.product) {
			const product = extendProductInformationWIthConstants(data.product);
			const event: DataLayerObject = {
				product,
				event: data.type,
			};
			if (data.order) {
				event.order = data.order;
			}
			if (data.totalPrice) {
				event.update = { totalPrice: data.totalPrice };
			}
			window.dataLayer.push(event);
		}
	}
};

export const trackCommerceGoogleAnalytics = (data: ICommerceEventGoogleAnalytics) => {
	if (window.dataLayer) {
		if (data.product) {
			const event = {
				event: 'EEC_purchase',
				event_name: 'purchase',
				ecommerce: { ...data.order, items: data.product },
			};

			window.dataLayer.push(event);
		}
	}
};

export const trackTeaserClick = (data: ClickEvent) => {
	trackEvent({
		type: 'click',
		interaction: 'button_click',
		...data,
	});
};

export const trackFunnelCta = (data: ClickEvent) => {
	trackEvent({
		type: 'click',
		interaction: 'funnel_cta',
		...data,
	});
};

export const trackButtonClick = (data: ClickEvent) => {
	trackEvent({
		type: 'click',
		interaction: 'button_click',
		...data,
	});
};

export const trackContactClick = (data: ClickEvent) => {
	trackEvent({
		type: 'click',
		interaction: 'contact_click',
		...data,
	});
};

export const trackIconClick = (data: ClickEvent) => {
	trackEvent({
		type: 'click',
		interaction: 'icon_click',
		...data,
	});
};

export const trackInfoClick = (data: ClickEvent) => {
	trackEvent({
		type: 'click',
		interaction: 'info_click',
		...data,
	});
};

export const trackFunnelView = (data: ClickEvent) => {
	trackEvent({
		type: 'click',
		interaction: 'funnel_view',
		...data,
	});
};

export const trackFunnelStart = (data: ClickEvent) => {
	trackEvent({
		type: 'click',
		interaction: 'funnel_start',
		...data,
	});
};

export const trackTeaserView = (data: ClickEvent) => {
	trackEvent({
		type: 'click',
		interaction: 'teaser_view',
		...data,
	});
};

export const trackFunnelClick = (data: ClickEvent) => {
	trackEvent({
		type: 'click',
		interaction: 'funnel_click',
		...data,
	});
};

export const trackInteractionClick = (data: ClickEvent) => {
	trackEvent({
		type: 'click',
		interaction: 'interaction_click',
		...data,
	});
};

export const trackLinkClick = (data: ClickEvent) => {
	trackEvent({
		type: 'click',
		interaction: 'link_click',
		...data,
	});
};
export const trackChange = (data: ChangeEvent) => {
	trackEvent({
		type: 'click',
		interaction: 'change',
		...data,
	});
};

export const productTrack = (
	data: ICommerceInput[],
	eventType: DataLayerEventName,
	orderDetail?: OptionalCommerceData,
) => {
	trackCommerce({
		type: eventType,
		product: data,
		...orderDetail,
	});
};

export const googleAnalyticsProductTrack = (
	data: ICommerceInputGoogleAnalytics[],
	eventType: DataLayerEventName,
	orderDetail?: OptionalCommerceDataGoogleAnalytics,
) => {
	trackCommerceGoogleAnalytics({
		type: eventType,
		product: data,
		...orderDetail,
	});
};

export const gatherProductTrackingDataGoogleAnalytics = ({
	offers,
	status,
	additionalData,
	orderData,
}: IProductTrackingDataProps) => {
	// Currently Google Analytics is only used for product_order tracking
	if (status !== 'order') return;

	const productsTrackingData: ICommerceInputGoogleAnalytics[] = [];
	let orderDetail: OptionalCommerceDataGoogleAnalytics | undefined = undefined;
	const memberDiscount = sessionStorage.getItem('memberDiscount');
	const productsWithMemberPrice = offers.filter(({ supplierId }) => supplierId !== 3);

	offers?.forEach((offer) => {
		if (offer) {
			let membershipBenefit: AdditionalInputGoogleAnalytics | undefined = undefined;

			if (memberDiscount && offer.supplierId !== 3) {
				membershipBenefit = {
					discount: (Number(memberDiscount) / productsWithMemberPrice?.length).toFixed(2).replace(',', '.'),
					coupon: 'ADAC Vorteil',
				};
			}

			productsTrackingData.push({
				item_name: productNameMap[offer.supplierId],
				item_id: offer.serviceCatalogName,
				price: (
					offer.prices?.find((price) => price.type === 'total')?.amount?.amount || offer.price.amount
				).replace(',', '.'),
				currency:
					offer.prices?.find((price) => price.type === 'total')?.amount?.currency || offer.price.currency,
				item_variant: `${offer.moduleTypeId === 3 ? '' : `${offer.moduleDetail}`}${
					offer.sectionName ? `${offer.sectionName} | ` : ''
				}${offer.moduleTypeId === 3 ? `${offer.trips} ${offer.trips > 1 ? 'Fahrten' : 'Fahrt'}` : ''}`,
				item_category: `digitale ${offer.moduleType}`,
				quantity: 1,
				item_type: 'Mautportal',
				item_brand: productSupplierMap[offer.supplierId],
				...additionalData,
				...membershipBenefit,
			});
		}
	});

	if (orderData) {
		orderDetail = {
			order: {
				transaction_id: orderData.orderNo,
				value: orderData.price.amount.replace(',', '.'),
				payment_type: sessionStorage.getItem('paymentMethod') || '',
				currency: orderData.price.currency,
			},
		};
		if (orderDetail.order && orderData.orderDiscount) {
			orderDetail.order.coupon = 'ADAC Vorteil';
		}
	}

	googleAnalyticsProductTrack(productsTrackingData, `product_${status}`, orderDetail);
};

export const gatherProductTrackingData = ({
	offers,
	status,
	additionalData,
	orderData,
	updatedPrice,
}: IProductTrackingDataProps) => {
	gatherProductTrackingDataGoogleAnalytics({
		offers,
		status,
		additionalData,
		orderData,
		updatedPrice,
	});

	const productsTrackingData: ICommerceInput[] = [];
	let orderDetail: OptionalCommerceData | undefined = undefined;
	const memberDiscount = sessionStorage.getItem('memberDiscount');
	const productsWithMemberPrice = offers.filter(({ supplierId }) => supplierId !== 3);
	offers?.forEach((offer) => {
		if (offer) {
			let membershipBenefit: AdditionalInput | undefined = undefined;
			if (memberDiscount && offer.supplierId !== 3) {
				membershipBenefit = {
					productDiscount: (Number(memberDiscount) / productsWithMemberPrice?.length)
						.toFixed(2)
						.replace('.', ','),
					productVoucher: 'ADAC Vorteil',
				};
			}
			productsTrackingData.push({
				productName: productNameMap[offer.supplierId],
				productID: offer.serviceCatalogName,
				productStatus: status,
				productPrice:
					offer.prices?.find((price) => price.type === 'total')?.amount?.amount || offer.price.amount,
				productVariant: `${offer.moduleTypeId === 3 ? '' : `${offer.moduleDetail}`}${
					offer.sectionName ? `${offer.sectionName} | ` : ''
				}${offer.moduleTypeId === 3 ? `${offer.trips} ${offer.trips > 1 ? 'Fahrten' : 'Fahrt'}` : ''}`,
				productCategory: `digitale ${offer.moduleType}`,
				productQty: 1,
				productType: 'Mautportal',
				contractType: 'einmaliger Kauf',
				...additionalData,
				...membershipBenefit,
			});
		}
	});

	if (status === 'update' && updatedPrice) {
		orderDetail = { totalPrice: updatedPrice };
	}

	if (orderData) {
		orderDetail = {
			order: {
				orderID: orderData.orderNo,
				orderTotalPrice: orderData.price.amount,
				orderStatus: 'confirmed',
				orderPaymentMethode: sessionStorage.getItem('paymentMethod') || '',
			},
		};
		if (orderDetail.order && orderData.orderDiscount) {
			orderDetail.order.orderDiscount = orderData.orderDiscount;
		}
	}

	productTrack(productsTrackingData, `product_${status}`, orderDetail);
};

export const formatOrderTrackingData = (orderInfo: ICartOrderInfo) => {
	const memberDiscount = sessionStorage.getItem('memberDiscount'); //only if the customer is a member and vendor gives membership benefits
	let trackingData: IProductTrackingDataProps = {
		offers: orderInfo?.orderDetails,
		status: 'order' as CommerceStatus,
		orderData: orderInfo,
	};
	if (memberDiscount) {
		trackingData = {
			...trackingData,
			orderData: {
				...orderInfo,
				orderDiscount: memberDiscount.replace('.', ','),
			},
		};
	}
	return trackingData;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withTracking = <T extends Record<string, any>>(WrappedComponent: React.FC<T>) => {
	const WithTracking = ({
		tracking = {},
		...props
	}: T &
		WithTrackingProps & {
			onClick?: React.MouseEventHandler;
			onChange?: React.ChangeEventHandler;
		}) => {
		const { onClick, onChange, ...propsWithoutEventHandlers } = props;

		useEffect(() => {
			if (tracking.funnel_start) {
				trackFunnelStart(tracking.funnel_start);
			}
			if (tracking.funnel_view) {
				trackFunnelView(tracking.funnel_view);
			}
			if (tracking.teaser_view) {
				trackTeaserView(tracking.teaser_view);
			}
		}, []);

		const clickHandler: React.MouseEventHandler = (e) => {
			onClick?.(e);

			if (tracking.info_click) {
				trackInfoClick(tracking.info_click);
			}
			if (tracking.icon_click) {
				trackIconClick(tracking.icon_click);
			}
			if (tracking.funnel_click) {
				trackFunnelClick(tracking.funnel_click);
			}
			if (tracking.button_click) {
				trackButtonClick(tracking.button_click);
			}
			if (tracking.teaser_click) {
				trackTeaserClick(tracking.teaser_click);
			}
			if (tracking.funnel_cta) {
				trackFunnelCta(tracking.funnel_cta);
			}
			if (tracking.link_click) {
				trackLinkClick(tracking.link_click);
			}
			if (tracking.contact_click) {
				trackContactClick(tracking.contact_click);
			}
			if (tracking.interaction_click) {
				trackInteractionClick(tracking.interaction_click);
			}
		};

		const isSelectElement = (
			element: HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement,
		): element is HTMLSelectElement => 'options' in element;

		const changeHandler: React.ChangeEventHandler<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement> = (
			e,
		) => {
			if (onChange) {
				onChange(e);
			}

			if (tracking.change) {
				if (isSelectElement(e.currentTarget)) {
					trackChange({
						...tracking.change,
						label: e.currentTarget.options[e.currentTarget.selectedIndex].text,
					});
				} else {
					trackChange({ ...tracking.change, label: e.currentTarget.value });
				}
			}
		};

		return (
			<WrappedComponent
				data-tracking={JSON.stringify(tracking)}
				onClick={clickHandler}
				onChange={changeHandler}
				{...(propsWithoutEventHandlers as T)}
			/>
		);
	};

	return WithTracking;
};
